// マイク音声キャプチャ + RMSベースの発話検知
// （saleseed-ai-roleplay hooks/use-audio-capture.ts から移植）
//
// ターン終了は無音による自動判定ではなく、候補者の「話し終わりました」ボタン（finish()）で
// 明示的に行う。長く話したい人・考えながら話す人が途中で切られないようにするため。
// RMSは発話開始の検知（UI表示）と先頭無音のトリムにのみ使う。
//
// データの流れ:
//   マイク → AudioContext(16kHz) → AudioWorkletNode(/pcm-processor.js)
//   → postMessage → このクラス → finish() → onSpeechEnd(base64 PCM)

export const CAPTURE_SAMPLE_RATE = 16000

const DEFAULTS = {
  silenceRmsThreshold: 500, // これ以上のRMSを「発話中」とみなす（発話開始検知・先頭無音トリム用）
  minSpeechChunks: 12       // これ未満のチャンク数はノイズとして破棄（1チャンク≒8ms）
}

// PCM 16bit の ArrayBuffer から RMS（音量）を計算する
const calculateRMS = (buffer) => {
  const int16 = new Int16Array(buffer)
  let sum = 0
  for (let i = 0; i < int16.length; i++) sum += int16[i] * int16[i]
  return Math.sqrt(sum / int16.length)
}

export class AudioCapture {
  constructor({ onSpeechEnd, onSpeechStart = null, ...settings }) {
    this.onSpeechEnd = onSpeechEnd
    this.onSpeechStart = onSpeechStart
    this.settings = { ...DEFAULTS, ...settings }

    this.mediaStream = null
    this.audioContext = null
    this.workletNode = null

    this.listening = false
    this.chunks = []
    this.speechDetected = false
  }

  // マイク権限を事前取得する（同意画面・開始ボタンで呼び、開始時の遅延と失敗を早期検知）
  async requestPermission() {
    if (this.mediaStream) return
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: CAPTURE_SAMPLE_RATE,
        channelCount: 1,
        echoCancellation: true, // スピーカーから出るAI音声の回り込み対策
        noiseSuppression: true,
        autoGainControl: true
      }
    })
  }

  async start() {
    await this.requestPermission()

    this.audioContext = new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE })
    await this.audioContext.audioWorklet.addModule("/pcm-processor.js")

    const source = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-processor")
    source.connect(this.workletNode)

    // destinationに繋がないと一部ブラウザでデータが流れない。
    // 直接繋ぐとマイク音が聞こえるため音量0のGainNodeを経由する。
    const silentGain = this.audioContext.createGain()
    silentGain.gain.value = 0
    this.workletNode.connect(silentGain)
    silentGain.connect(this.audioContext.destination)

    this.workletNode.port.onmessage = (event) => this.handleChunk(event)
  }

  // 聞き取りのON/OFF。AI読み上げ中はOFFにしてエコーや雑音の蓄積を防ぐ。
  setListening(value) {
    this.listening = value
    this.resetBuffer()
  }

  // 「話し終わりました」ボタンによる明示的なターン終了。
  // 蓄積した発話を送信する。発話が短すぎる/無い場合は空文字でコールバックし、
  // 呼び出し側で「聞き取れませんでした」扱いにする。
  finish() {
    if (!this.listening) return

    const hasSpeech = this.speechDetected && this.chunks.length >= this.settings.minSpeechChunks
    const base64 = hasSpeech ? this.flush() : ""
    this.listening = false
    this.resetBuffer()
    this.onSpeechEnd(base64)
  }

  stop() {
    this.workletNode?.disconnect()
    this.workletNode = null
    this.audioContext?.close()
    this.audioContext = null
    this.mediaStream?.getTracks().forEach((track) => track.stop())
    this.mediaStream = null
    this.resetBuffer()
  }

  // --- private ---

  handleChunk(event) {
    if (event.data.type !== "audio" || !this.listening) return

    const buffer = event.data.buffer
    const rms = calculateRMS(buffer)

    if (rms >= this.settings.silenceRmsThreshold) {
      if (!this.speechDetected) this.onSpeechStart?.()
      this.speechDetected = true
    }

    // 喋り始めてからのチャンクのみ蓄積（先頭の無音は捨てる）。
    // 一度話し始めたら、考え込む間（無音）も含めてfinish()まで蓄積し続ける。
    if (this.speechDetected) this.chunks.push(buffer)
  }

  flush() {
    const chunks = this.chunks
    this.resetBuffer()
    if (chunks.length === 0) return ""

    const byteArrays = chunks.map((c) => new Uint8Array(c))
    const totalLength = byteArrays.reduce((sum, a) => sum + a.length, 0)
    const combined = new Uint8Array(totalLength)
    let offset = 0
    for (const arr of byteArrays) {
      combined.set(arr, offset)
      offset += arr.length
    }

    // base64変換（AudioWorkletスレッドにbtoaがないためメインスレッドで行う）
    let binary = ""
    const CHUNK = 0x8000
    for (let i = 0; i < combined.length; i += CHUNK) {
      binary += String.fromCharCode.apply(null, combined.subarray(i, i + CHUNK))
    }
    return btoa(binary)
  }

  resetBuffer() {
    this.chunks = []
    this.speechDetected = false
  }
}
